<template>
  <b-overlay
    :show="show"
    variant="transparent"
    opacity="0.99"
    blur="5px"
    rounded="sm"
  >
    <template #overlay>
      <div class="text-center">
        <b-icon-controller
          font-scale="3"
          animation="cylon"
        />
        <p id="cancel-label">
          กรุณารอสักครู่...
        </p>
      </div>
    </template>

    <!-- Filter Bar -->
    <div class="card">
      <div class="card-body pt-2">
        <div class="row align-items-end">
          <div class="col-md-2">
            <div class="form-group mb-1">
              <label>สถานะ</label>
              <b-form-select
                v-model="search_status"
                :options="statusOPs"
              />
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group mb-1">
              <label>วันที่เริ่มต้น</label>
              <flat-pickr
                v-model="dateStart"
                class="form-control"
                placeholder="เลือกเวลาเริ่มต้น"
                :config="fpConfig"
              />
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group mb-1">
              <label>วันที่สิ้นสุด</label>
              <flat-pickr
                v-model="dateEnd"
                class="form-control"
                placeholder="เลือกเวลาสิ้นสุด"
                :config="fpConfig"
              />
            </div>
          </div>

          <div class="col-md-2 d-flex gap-1 align-items-center">
            <div class="mr-2">
              <label class="d-block">Auto Refresh</label>
              <b-form-checkbox
                v-model="status_interval"
                switch
                size="lg"
                :value="true"
                :unchecked-value="false"
              />
            </div>
            <div class="flex-grow-1 text-right">
              <button
                class="btn btn-light mr-1"
                @click="clearFilter()"
              >
                Clear
              </button>
              <button
                class="btn btn-gradient-dark"
                @click="getDeposit()"
              >
                <feather-icon icon="SearchIcon" />
                Search
              </button>
            </div>
          </div>
        </div>

        <!-- Status Tabs -->
        <div class="mt-3">
          <div
            class="btn-group btn-group-sm"
            role="group"
          >
            <button
              v-for="op in tabStatus"
              :key="op.value"
              class="btn"
              :class="tabButtonClass(op.value)"
              @click="setTab(op.value)"
            >
              {{ op.text }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table + Detail -->
    <b-card
      no-body
      class="mt-2"
    >
      <b-card-body class="pt-2 pb-0" />
      <b-table
        small
        striped
        hover
        responsive
        show-empty
        class="position-relative items-center"
        :per-page="perPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :filter="filter"
        :filter-included-fields="filterOn"
        :tbody-tr-class="rowClass"
        @sort-changed="getDeposit"
      >
        <!-- Running No. -->
        <template #cell(index)="data">
          {{ perPage * (currentPage - 1) + (data.index + 1) }}
        </template>

        <!-- Empty -->
        <template #empty="scope">
          <div class="p-2 text-center">
            <img
              src="/box.png"
              alt="empty"
              height="40px"
            >
            {{ scope.emptyFiltered ? 'No matching records found' : 'ไม่พบข้อมูล' }}
          </div>
        </template>

        <!-- fullname -->
        <template #cell(fullname)="{ item }">
          {{ [item.firstNameTh, item.lastNameTh].filter(Boolean).join(' ') || '-' }}
        </template>

        <!-- amount -->
        <template #cell(amount)="{ item }">
          {{ formatAmount(item.amount) }}
        </template>

        <!-- status badge -->
        <template #cell(status)="{ item }">
          <span
            class="badge px-2 py-1"
            :class="statusClass(item.status)"
          >
            {{ item.status }}
          </span>
        </template>

        <!-- process -->
        <template #cell(process)="{ item }">
          <span class="text-mono">{{ item.process || '-' }}</span>
        </template>

        <!-- timeoutAt -->
        <template #cell(timeoutAt)="{ item }">
          {{ formatDate(item.timeOutDate || item.expiredDate) }}
        </template>

        <!-- updated -->
        <template #cell(updatedDate)="{ item }">
          {{ item.updatedDate && item.updatedDate !== '-' ? formatDate(item.updatedDate) : '-' }}
        </template>

        <!-- remark -->
        <template #cell(remark)="{ item }">
          <span
            class="d-inline-block text-truncate"
            style="max-width: 220px;"
          >
            {{ item.remark || '-' }}
          </span>
        </template>

        <!-- Manage buttons -->
        <template #cell(manage)="{ item }">
          <div class="btn-group btn-group-sm">
            <button
              class="btn btn-outline-info mr-1"
              :disabled="item.status === 'SUCCESS'"
              @click="onManual(item)"
            >
              Manual
            </button>

            <button
              class="btn btn-outline-success mr-1"
              :disabled="!item.matchedLog"
              @click="onDetail(item)"
            >
              Detail
            </button>

            <button
              class="btn btn-outline-warning mr-1"
              :disabled="item.status !== 'TIMEOUT'"
              @click="onReset(item)"
            >
              Reset
            </button>

            <button
              class="btn btn-outline-danger"
              :disabled="item.status === 'CANCEL'"
              @click="onCancel(item)"
            >
              Cancel
            </button>
          </div>
        </template>
      </b-table>

      <!-- Detail Panel -->
      <div
        v-if="selectedItem"
        class="withdraw-detail-wrapper"
      >
        <b-card class="mt-3 shadow-sm">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h5 class="mb-0">
                Detail :
                <span class="text-primary">
                  {{ selectedItem.ref || selectedItem.id }}
                </span>
              </h5>
              <small class="text-muted">
                Username : {{ selectedItem.username }} | Amount :
                {{ formatAmount(selectedItem.amount) }}
              </small>
            </div>

            <div class="text-right">
              <span
                v-if="detailLoading"
                class="text-muted small mr-2"
              >
                <b-spinner
                  small
                  class="mr-1"
                />
                กำลังโหลดรายละเอียด...
              </span>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="closeDetail"
              >
                ปิด
              </button>
            </div>
          </div>

          <!-- Loading -->
          <div
            v-if="detailLoading"
            class="text-center py-4"
          >
            <b-spinner class="mb-2" />
            <div class="text-muted">
              ดึงข้อมูลรายละเอียดจาก AutoPeer ...
            </div>
          </div>

          <!-- มีข้อมูล -->
          <div v-else-if="detailData">
            <!-- Top summary table -->
            <b-table
              small
              bordered
              hover
              :items="detailData.logs"
              :fields="detailTableFields"
              class="mb-3 detail-top-table"
            >
              <template #cell(statusText)="{ item }">
                <span
                  class="badge px-3 py-1"
                  :class="detailStatusClass(item.statusText)"
                >
                  {{ item.statusText }}
                </span>
              </template>
            </b-table>

            <!-- User Withdraw Data + Slip Images รวมกัน -->
            <div class="border rounded p-3 bg-light detail-user-box">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 font-weight-bold">
                  User Withdraw Data
                </h6>

                <span
                  v-if="detailData.slips && detailData.slips.length"
                  class="badge badge-pill badge-secondary"
                >
                  Slip {{ detailData.slips.length }}
                </span>
              </div>

              <!-- ส่วนข้อมูลผู้โอนเงิน -->
              <div class="row">
                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    BANK NAME :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.bankNameTh || '-' }}
                  </div>
                </div>

                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    BANK ACCOUNT :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.bankAccount || '-' }}
                  </div>
                </div>

                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    PROMPTPAY :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.promptpay || '-' }}
                  </div>
                </div>

                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    FIRSTNAME (TH) :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.firstNameTh || '-' }}
                  </div>
                </div>

                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    LASTNAME (TH) :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.lastNameTh || '-' }}
                  </div>
                </div>

                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    FIRSTNAME (EN) :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.firstNameEn || '-' }}
                  </div>
                </div>

                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    LASTNAME (EN) :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.lastNameEn || '-' }}
                  </div>
                </div>

                <div class="col-md-4 col-6 mb-2">
                  <div class="text-label">
                    USERNAME :
                  </div>
                  <div class="text-value">
                    {{ detailData.user.username || selectedItem.username }}
                  </div>
                </div>
              </div>

              <!-- เส้นแบ่งเล็กๆ ก่อนส่วนรูปสลิป -->
              <hr
                v-if="detailData.slips && detailData.slips.length"
                class="my-3"
              >

              <!-- ส่วนรูป Slip Images อยู่ในกล่องเดียวกัน -->
              <div
                v-if="detailData.slips && detailData.slips.length"
                class="detail-slip-box"
              >
                <div class="row">
                  <div
                    v-for="s in detailData.slips"
                    :key="s.url"
                    class="col-md-3 col-sm-4 col-6 mb-3"
                  >
                    <div class="slip-card">
                      <a
                        :href="s.url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          :src="s.url"
                          alt="slip"
                          class="img-fluid slip-img"
                        >
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- ไม่พบข้อมูล -->
          <div
            v-else
            class="text-center text-muted py-3"
          >
            ไม่พบข้อมูลรายละเอียดของรายการนี้
          </div>
        </b-card>
      </div>

      <!-- footer -->
      <b-card-body class="d-flex justify-content-between flex-wrap pt-2">
        <b-form-group
          label="Per Page"
          label-cols="8"
          label-align="left"
          label-size="sm"
          label-for="perPageSelect"
          class="text-nowrap mb-md-0 mr-1"
        >
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            inline
            :options="pageOptions"
            @change="getDeposit()"
          />
        </b-form-group>

        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
            @input="getDeposit()"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </div>
      </b-card-body>
    </b-card>
  </b-overlay>
</template>

<script>
import moment from 'moment-timezone'
import {
  BOverlay,
  BIconController,
  BCard,
  BTable,
  BFormGroup,
  BFormSelect,
  BPagination,
  BCardBody,
  VBToggle,
  BFormCheckbox,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import flatPickr from 'vue-flatpickr-component'

// —— helper badge mapping
const STATUS_COLORS = {
  PENDING: 'badge-warning',
  WAITING: 'badge-info',
  SUCCESS: 'badge-success',
  CANCEL: 'badge-secondary',
  TIMEOUT: 'badge-danger',
  'ON HOLD': 'badge-dark',
  PROCESS: 'badge-primary',
}

export default {
  components: {
    flatPickr,
    BCard,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BFormCheckbox,
    BCardBody,
    BOverlay,
    BIconController,
    BSpinner,
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  data() {
    const today = moment().tz('Asia/Bangkok').format('YYYY-MM-DD')
    return {
      // overlay
      show: true,

      // table core
      items: [],
      totalRows: 0,
      perPage: 20,
      pageOptions: [10, 15, 20, 50, 100],
      currentPage: 1,
      sortBy: 'createdDate',
      sortDesc: true,
      sortDirection: 'desc',
      filter: null,
      filterOn: [],

      // filters
      search_val: null,
      search_status: '',
      dateStart: `${today} 00:00`,
      dateEnd: `${today} 23:59`,
      fpConfig: { enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true },

      // interval
      status_interval: false,
      interval: null,

      // dropdowns
      statusOPs: [
        { value: '', text: 'ทั้งหมด' },
        { value: 'PENDING', text: 'รอดำเนินการ' },
        { value: 'WAITING', text: 'รอ Manual < 1%' },
        { value: 'SUCCESS', text: 'สำเร็จ' },
        { value: 'CANCEL', text: 'ยกเลิก' },
        { value: 'TIMEOUT', text: 'หมดเวลา' },
        { value: 'ON HOLD', text: 'พักไว้/ชื่อไม่ตรง' },
      ],

      tabStatus: [
        { value: '', text: 'All' },
        { value: 'PENDING', text: 'Pending' },
        { value: 'WAITING', text: 'Waiting' },
        { value: 'TIMEOUT', text: 'Timeout' },
        { value: 'SUCCESS', text: 'Success' },
        { value: 'CANCEL', text: 'Cancel' },
        { value: 'ON HOLD', text: 'On Hold' },
      ],

      // raw list (optional keep)
      depositdata: [],

      // table fields
      fields: [
        {
          key: 'index', label: 'No.', class: 'text-nowrap', thClass: 'text-center', tdClass: 'text-center',
        },
        { key: 'username', label: 'Username', sortable: true },
        { key: 'firstNameTh', label: 'ชื่อ' },
        { key: 'lastNameTh', label: 'นามสกุล' },
        {
          key: 'createdDate', label: 'Create Date', sortable: true, formatter: v => this.formatDate(v),
        },
        { key: 'status', label: 'Status', sortable: true },
        {
          key: 'baseAmount', label: 'ยอดถอน', sortable: true, class: 'text-right',
        },
        {
          key: 'amount', label: 'ยอดค้างถอน', sortable: true, class: 'text-right',
        },
        // {
        //   key: 'succeedByAutoPeer', label: 'Succeed By AutoPeer', sortable: false, formatter: v => v ?? 0, class: 'text-right',
        // },
        // {
        //   key: 'succeedByManually', label: 'Succeed By Manually', sortable: false, formatter: v => v ?? 0, class: 'text-right',
        // },
        {
          key: 'manual', label: 'Manual', sortable: false, formatter: v => (v || 0), class: 'text-right',
        },
        {
          key: 'process', label: 'Process', sortable: false, class: 'text-mono text-center',
        },
        {
          key: 'timeoutAt', label: 'Timeout At', sortable: true, formatter: (_, item) => this.formatDate(item.timeOutDate || item.expiredDate),
        },
        { key: 'updatedDate', label: 'Edited by / Updated', sortable: true },
        {
          key: 'manage', label: 'Manage', tdClass: 'text-nowrap text-right', thClass: 'text-right',
        },
      ],

      // ===== Detail state =====
      selectedItem: null,
      detailLoading: false,
      detailData: null,
      detailTableFields: [
        {
          key: 'no', label: 'No', thClass: 'text-center', tdClass: 'text-center text-nowrap',
        },
        { key: 'ref', label: 'Transaction Ref', class: 'text-nowrap' },
        { key: 'amount', label: 'Amount', class: 'text-right text-nowrap' },
        { key: 'statusText', label: 'Status', class: 'text-center text-nowrap' },
        { key: 'createdAt', label: 'Create Date', class: 'text-center text-nowrap' },
        { key: 'successAt', label: 'Success', class: 'text-center text-nowrap' },
      ],
    }
  },
  computed: {
    sortOptions() {
      return this.fields.filter(f => f.sortable).map(f => ({ text: f.label, value: f.key }))
    },
  },
  created() {
    // auto-refresh each 30s when toggled
    this.interval = setInterval(() => {
      if (this.status_interval) this.getDepositSilently()
    }, 30000)
  },
  destroyed() {
    clearInterval(this.interval)
  },
  mounted() {
    this.getDeposit()
  },
  methods: {
    // ===== UI Helpers =====
    statusClass(status) {
      return STATUS_COLORS[status] || 'badge-secondary'
    },
    detailStatusClass(text = '') {
      const t = String(text).toUpperCase()
      if (t.includes('AUTOPEER')) return 'badge-success'
      if (t.includes('MANUAL')) return 'badge-primary'
      if (t.includes('TIMEOUT') || t.includes('CANCEL')) return 'badge-danger'
      return 'badge-secondary'
    },
    tabButtonClass(val) {
      return {
        'btn-outline-secondary': this.search_status !== val,
        'btn-success': this.search_status === val,
      }
    },
    setTab(val) {
      this.search_status = val
      this.currentPage = 1
      this.getDeposit()
    },
    rowClass(item) {
      if (!item) return ''
      // if (item.status === 'TIMEOUT') return 'table-danger'
      // if (item.status === 'WAITING') return 'table-warning'
      // if (item.status === 'PENDING') return 'table-info'
      return ''
    },
    clearFilter() {
      const today = moment().tz('Asia/Bangkok').format('YYYY-MM-DD')
      this.search_val = null
      this.search_status = ''
      this.dateStart = `${today} 00:00`
      this.dateEnd = `${today} 23:59`
      this.currentPage = 1
      this.getDeposit()
    },

    closeDetail() {
      this.selectedItem = null
      this.detailData = null
      this.detailLoading = false
    },

    // ===== Formatters =====
    formatDate(v) {
      if (!v || v === '-') return '-'
      return moment(v).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss')
    },
    formatAmount(v) {
      const n = Number(v || 0)
      return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    // ===== Data Fetching =====
    getDepositSilently() {
      const old = this.show
      this.show = false
      this.getDeposit().finally(() => { this.show = old })
    },
    async getDeposit() {
      this.show = false
      const URL = '/autopeer/withdraw'
      const params = {
        page: this.currentPage,
        perPage: this.perPage,
        startDate: this.dateStart,
        endDate: this.dateEnd,
        status: this.search_status || '',
        ref: this.ref || '',
        type: 'SELLER',
        search_val: this.search_val,
      }
      try {
        const { data: res } = await this.$http.get(URL, { params })
        const list = Array.isArray(res.data) ? res.data : []
        this.depositdata = list
        this.onFiltered({
          data: list.map(this.mapRow),
          total: Number(res.total || list.length),
        })
      } catch (e) {
        console.error(e)
        this.SwalError('ไม่สามารถโหลดรายการถอนได้')
      } finally {
        this.show = false
      }
    },
    mapRow(it) {
      const cloned = { ...it }
      cloned.timeoutAt = it.timeOutDate || it.expiredDate || null
      cloned.succeedByAutoPeer = it.succeedByAutoPeer ?? 0
      cloned.succeedByManually = it.succeedByManually ?? 0
      cloned.manual = it.isManual ? Number(it.amount || 0) : (it.manual ?? 0)
      return cloned
    },

    onFiltered(payload) {
      this.items = payload.data
      this.totalRows = payload.total
    },

    // ===== Manage Handlers =====
    async onManual(item) {
      if (!item) return

      const confirm = await this.$swal({
        icon: 'warning',
        title: '<h3 style="color:#141414">ยืนยันทำ Manual</h3>',
        html: `
      ต้องการทำรายการถอนแบบ <b>Manual</b>?<br>
      <div style="margin-top:10px; color:#666">
        Ref: <b>${item.ref}</b><br>
        Username: <b>${item.username}</b><br>
        Amount: <b>${item.amount}</b>
      </div>
    `,
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
        reverseButtons: false,
        customClass: {
          confirmButton: 'btn btn-success mr-1',
          cancelButton: 'btn btn-secondary',
        },
      })

      if (!confirm.isConfirmed) return

      try {
        const payload = {
          id: item.id,
          ref: item.ref,
          bankName: item.bankName,
        }

        const { data: res } = await this.$http.post('/autopeer/withdrawmanual', payload)

        if (res.code !== 0) {
          return this.SwalError(`Error: ${res.message || 'ทำรายการไม่สำเร็จ'}`)
        }

        const inner = res.data
        if (inner?.code && inner.code !== '0') {
          return this.SwalError(`(${inner.code}) ${inner.message || 'ทำรายการไม่สำเร็จ'}`)
        }

        this.Success('ทำรายการ Manual สำเร็จแล้ว')
        this.getDeposit()
      } catch (err) {
        console.error(err)
        this.SwalError(err?.response?.data?.message || 'เกิดข้อผิดพลาดขณะทำรายการ Manual')
      }
    },

    // ===== Detail =====
    async onDetail(item) {
      if (!item) return

      this.selectedItem = item
      this.detailData = null
      this.detailLoading = true

      try {
        // 1) matchedLog ของฝั่ง "ผู้โอนเงินให้"
        const rawLogs = Array.isArray(item.matchedLog) ? item.matchedLog : []

        // base status text (Autopeer / Manual ฯลฯ)
        // eslint-disable-next-line no-nested-ternary
        const statusTextBase = item.isManual
          ? 'Succeed By Manually'
          : (item.status === 'SUCCESS' ? 'Succeed By Autopeer' : item.status || '-')

        // 2) map logs สำหรับตารางด้านบน (รองรับหลาย matchedLog)
        let logs = []

        if (rawLogs.length) {
          logs = rawLogs.map((log, idx) => ({
            no: idx + 1,
            ref: log.ref || item.ref || '-',
            amount: this.formatAmount(
              log.amount ?? log.baseAmount ?? item.baseAmount ?? item.amount,
            ),
            statusText: statusTextBase,
            // eslint-disable-next-line no-nested-ternary
            createdAt: log.createdDate
              ? this.formatDate(log.createdDate)
              : (item.createdDate ? this.formatDate(item.createdDate) : '-'),
            // eslint-disable-next-line no-nested-ternary
            successAt: log.updatedDate
              ? this.formatDate(log.updatedDate)
              : (item.updatedDate ? this.formatDate(item.updatedDate) : '-'),
          }))
        } else {
          // ถ้าไม่มี matchedLog เลย -> ใช้ข้อมูลจาก item เอง 1 แถว
          logs = [
            {
              no: 1,
              ref: item.ref || '-',
              amount: this.formatAmount(item.baseAmount ?? item.amount),
              statusText: statusTextBase,
              createdAt: item.createdDate ? this.formatDate(item.createdDate) : '-',
              successAt: item.updatedDate ? this.formatDate(item.updatedDate) : '-',
            },
          ]
        }

        // 3) ผู้โอนเงินหลัก -> ใช้ log แรกเป็นตัวแทน (กรณีมีหลาย log)
        const donor = rawLogs[0] || item

        const user = {
          bankNameTh: donor.bankName,
          bankAccount: donor.accountNumber,
          promptpay: donor.promptPayID || donor.accountNumber,
          firstNameTh: donor.firstNameTh,
          lastNameTh: donor.lastNameTh,
          firstNameEn: donor.firstNameEn,
          lastNameEn: donor.lastNameEn,
          username: donor.username,
        }

        // 4) รวมรูป slip จาก matchedLog ทุกตัว
        //    slips: [{ url, ref, index, createdAt }, ...]
        const slips = rawLogs.flatMap((log, idx) => {
          if (!Array.isArray(log.imgSlip)) return []
          return log.imgSlip.map((url, i) => ({
            url,
            ref: log.ref || item.ref || '-',
            index: idx + 1,
            order: i + 1,
            createdAt: log.createdDate ? this.formatDate(log.createdDate) : '-',
          }))
        })

        this.detailData = {
          logs,
          user,
          slips,
        }
      } catch (err) {
        console.error('[Detail] map from matchedLog error:', err)
        this.SwalError('เกิดข้อผิดพลาดระหว่างเตรียมข้อมูลรายละเอียด')
      } finally {
        this.detailLoading = false
      }
    },

    async onReset(item) {
      const confirm = await this.$swal({
        icon: 'warning',
        title: '<h3 style="color:#141414">ยืนยันรีเซ็ตสถานะถอน</h3>',
        html: `
                คุณต้องการ <b>รีเซ็ตสถานะถอน</b> รายการนี้หรือไม่ ?<br>
                <div style="margin-top:10px; color:#666">
                  Ref: <b>${item.ref}</b><br>
                  Username: <b>${item.username}</b><br>
                  Amount: <b>${item.amount}</b>
                </div>
              `,
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
        reverseButtons: false,
        customClass: {
          confirmButton: 'btn btn-success mr-1',
          cancelButton: 'btn btn-secondary',
        },
      })

      if (!confirm.isConfirmed) return

      try {
        const payload = { ref: item.ref }

        const { data: res } = await this.$http.post('/autopeer/resetwithdraw', payload)

        if (res.code !== 0) {
          return this.SwalError(`Error: ${res.message || 'ไม่สามารถรีเซ็ตได้'}`)
        }

        const inner = res.data
        if (inner?.code && String(inner.code) !== '0') {
          return this.SwalError(`(${inner.code}) ${inner.message || 'ไม่สามารถรีเซ็ตได้'}`)
        }

        this.Success('รีเซ็ตสถานะถอนสำเร็จแล้ว')
        this.getDeposit()
      } catch (err) {
        console.error(err)
        this.SwalError(err?.response?.data?.message || 'เกิดข้อผิดพลาดขณะรีเซ็ตสถานะถอน')
      }
    },

    async onForceTimeout(item) {
      if (item.status !== 'PENDING') return
      try {
        // await this.$http.post('/autopeer/withdraw/force-timeout', { id: item.id })
        this.Success('Force Timeout สำเร็จ (demo)')
        this.getDeposit()
      } catch (e) {
        this.SwalError('Force Timeout ไม่สำเร็จ')
      }
    },
    async onCancel(item) {
      const confirm = await this.$swal({
        icon: 'warning',
        title: '<h3 style="color:#141414">ยืนยันยกเลิกรายการถอน</h3>',
        html: `
      คุณต้องการ <b>ยกเลิกรายการถอน</b> นี้หรือไม่ ?<br>
      <div style="margin-top:10px; color:#666">
        Ref: <b>${item.ref}</b><br>
        Username: <b>${item.username}</b><br>
        Amount: <b>${item.amount}</b>
      </div>
    `,
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
        reverseButtons: false,
        customClass: {
          confirmButton: 'btn btn-danger mr-1',
          cancelButton: 'btn btn-secondary',
        },
      })

      if (!confirm.isConfirmed) return

      try {
        const payload = { ref: item.ref }

        const { data: res } = await this.$http.post('/autopeer/cancelwithdraw', payload)

        if (res.code !== 0) {
          return this.SwalError(`Error: ${res.message || 'ไม่สามารถยกเลิกรายการได้'}`)
        }

        const inner = res.data
        if (inner?.code && String(inner.code) !== '0') {
          return this.SwalError(`(${inner.code}) ${inner.message || 'ไม่สามารถยกเลิกรายการได้'}`)
        }

        this.Success('ยกเลิกรายการถอนสำเร็จแล้ว')
        this.getDeposit()
      } catch (err) {
        console.error(err)
        this.SwalError(err?.response?.data?.message || 'เกิดข้อผิดพลาดขณะยกเลิกรายการถอน')
      }
    },

    // ===== Alerts =====
    Success(mes) {
      this.$swal({
        icon: 'success',
        title: '<h3 style="color: #141414">ทำรายการสำเร็จ</h3>',
        text: mes,
        customClass: { confirmButton: 'btn btn-success' },
      })
    },
    SwalError(mes) {
      this.$swal({
        icon: 'error',
        title: '<h3 style="color: #141414">เกิดข้อผิดพลาด!</h3>',
        text: mes,
        customClass: { confirmButton: 'btn btn-primary' },
      })
    },
  },
}
</script>

<style scoped>
table { width: 100%; }

/* สถานะสี (ซ้อนกับ badge-*) */
.badge {
  font-weight: 600;
  border-radius: 16px;
}
.text-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* ปุ่มแท็บสถานะ */
.btn-group .btn {
  min-width: 90px;
}

/* เน้นแถวตามสถานะ */
.table-danger  { background-color: rgba(220, 53, 69, .05) !important; }
.table-warning { background-color: rgba(255, 193, 7, .06) !important; }
.table-info    { background-color: rgba(23, 162, 184, .06) !important; }

/* หัวตาราง */
th {
  color: #fff;
  background-color: #000 !important;
}

/* Detail panel layout */
.withdraw-detail-wrapper {
  border-top: 1px solid #f1f1f1;
  padding: 0 1rem 1rem;
}

.detail-top-table >>> th {
  background-color: #f8f9fa !important;
  color: #495057 !important;
}

.detail-user-box {
  font-size: 0.9rem;
}

.text-label {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.text-value {
  font-weight: 500;
  color: #333;
}
.slip-card {
  background: #ffffff;
  border-radius: 10px;
  padding: 8px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.slip-meta {
  line-height: 1.2;
}

.slip-img {
  border-radius: 8px;
  max-height: 180px;
  object-fit: contain;
  width: 100%;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.slip-img:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.detail-slip-box {
  background-color: #f9fafb;
}

</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
